import React, { useState } from 'react';
import { XMarkIcon, CubeIcon, CreditCardIcon, UserIcon, MapPinIcon, CalendarIcon, CurrencyDollarIcon, EyeIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { useProdutosPedido, useContasReceber } from '../../hooks/usePedidos';
import type { PainelRegiao } from '../../types/pedidos';

interface PedidoDetalhesProps {
  pedido: PainelRegiao | null;
  onClose: () => void;
}

const PedidoDetalhes: React.FC<PedidoDetalhesProps> = ({ pedido, onClose }) => {
  const [activeTab, setActiveTab] = useState<'info' | 'produtos' | 'contas'>('info');

  // Hooks para buscar dados quando necessário. empresa vem do pedido — chave
  // composta no ERP (empresa, codigo).
  const { data: produtos, isLoading: loadingProdutos } = useProdutosPedido(pedido?.codigo || '', pedido?.empresa);
  const { data: contas, isLoading: loadingContas } = useContasReceber(pedido?.codigocliente || '');

  if (!pedido) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-1 sm:p-2 md:p-4">
      <div className="bg-white dark:bg-zinc-800 rounded-lg sm:rounded-2xl shadow-2xl max-w-sm sm:max-w-lg md:max-w-2xl lg:max-w-4xl w-full h-[95vh] sm:h-[90vh] md:h-[85vh] lg:h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-3 sm:p-4 md:p-6 border-b border-zinc-200 dark:border-zinc-700 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-zinc-800 dark:to-zinc-700 flex-shrink-0">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
            <div className="flex h-8 w-8 sm:h-10 sm:w-10 md:h-12 md:w-12 items-center justify-center rounded-full bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg flex-shrink-0">
              <CubeIcon className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <h2 className="text-base sm:text-lg md:text-xl font-bold text-zinc-800 dark:text-zinc-200 truncate">
                  Pedido #{pedido.codigo}
                </h2>
                {pedido.empresa && (
                  <span
                    className="inline-flex items-center rounded-md bg-zinc-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-zinc-600 dark:bg-zinc-700 dark:text-zinc-300"
                    title="Empresa do pedido"
                  >
                    Emp {pedido.empresa}
                  </span>
                )}
              </div>
              <p className="text-xs sm:text-sm text-zinc-600 dark:text-zinc-400 truncate">
                {pedido.cliente}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="flex h-8 w-8 sm:h-9 sm:w-9 md:h-10 md:w-10 items-center justify-center rounded-full bg-white dark:bg-zinc-700 text-zinc-600 dark:text-zinc-300 shadow-md hover:bg-zinc-50 dark:hover:bg-zinc-600 transition-colors flex-shrink-0"
          >
            <XMarkIcon className="h-4 w-4 sm:h-5 sm:w-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800/50 overflow-x-auto flex-shrink-0">
          <button
            onClick={() => setActiveTab('info')}
            className={`flex items-center gap-1 sm:gap-2 px-3 sm:px-4 md:px-6 py-2 sm:py-3 font-medium transition-colors whitespace-nowrap ${
              activeTab === 'info'
                ? 'border-b-2 border-blue-500 bg-white dark:bg-zinc-800 text-blue-600 dark:text-blue-400'
                : 'text-zinc-600 dark:text-zinc-400 hover:text-zinc-800 dark:hover:text-zinc-200'
            }`}
          >
            <EyeIcon className="h-3 w-3 sm:h-4 sm:w-4" />
            <span className="text-xs sm:text-sm">Informações</span>
          </button>
          <button
            onClick={() => setActiveTab('produtos')}
            className={`flex items-center gap-1 sm:gap-2 px-3 sm:px-4 md:px-6 py-2 sm:py-3 font-medium transition-colors whitespace-nowrap ${
              activeTab === 'produtos'
                ? 'border-b-2 border-blue-500 bg-white dark:bg-zinc-800 text-blue-600 dark:text-blue-400'
                : 'text-zinc-600 dark:text-zinc-400 hover:text-zinc-800 dark:hover:text-zinc-200'
            }`}
          >
            <CubeIcon className="h-3 w-3 sm:h-4 sm:w-4" />
            <span className="text-xs sm:text-sm">Produtos ({produtos?.length || 0})</span>
          </button>
          <button
            onClick={() => setActiveTab('contas')}
            className={`flex items-center gap-1 sm:gap-2 px-3 sm:px-4 md:px-6 py-2 sm:py-3 font-medium transition-colors whitespace-nowrap ${
              activeTab === 'contas'
                ? 'border-b-2 border-blue-500 bg-white dark:bg-zinc-800 text-blue-600 dark:text-blue-400'
                : 'text-zinc-600 dark:text-zinc-400 hover:text-zinc-800 dark:hover:text-zinc-200'
            }`}
          >
            <CreditCardIcon className="h-3 w-3 sm:h-4 sm:w-4" />
            <span className="text-xs sm:text-sm">Contas ({contas?.length || 0})</span>
          </button>
        </div>

        {/* Content */}
        <div className="p-3 sm:p-4 md:p-6 flex-1 overflow-y-auto">
          {activeTab === 'info' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
              {/* Informações do Pedido */}
              <div className="space-y-3 sm:space-y-4">
                <h3 className="text-base sm:text-lg font-semibold text-zinc-800 dark:text-zinc-200 flex items-center gap-2">
                  <CubeIcon className="h-4 w-4 sm:h-5 sm:w-5 text-blue-500" />
                  Dados do Pedido
                </h3>
                
                <div className="space-y-2 sm:space-y-3">
                  <div className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg bg-zinc-50 dark:bg-zinc-700/50">
                    <CalendarIcon className="h-3 w-3 sm:h-4 sm:w-4 text-zinc-500 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-xs sm:text-sm font-medium text-zinc-700 dark:text-zinc-300">Data</div>
                      <div className="text-xs sm:text-sm text-zinc-600 dark:text-zinc-400 truncate">{new Date(pedido.data).toLocaleDateString('pt-BR')}</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg bg-zinc-50 dark:bg-zinc-700/50">
                    <CurrencyDollarIcon className="h-3 w-3 sm:h-4 sm:w-4 text-emerald-500 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-xs sm:text-sm font-medium text-zinc-700 dark:text-zinc-300">Valor</div>
                      <div className="text-xs sm:text-sm text-emerald-600 dark:text-emerald-400 font-semibold truncate">
                        R$ {parseFloat(pedido.valorpedido?.toString() || '0').toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg bg-zinc-50 dark:bg-zinc-700/50">
                    <UserIcon className="h-3 w-3 sm:h-4 sm:w-4 text-blue-500 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-xs sm:text-sm font-medium text-zinc-700 dark:text-zinc-300">Vendedor</div>
                      <div className="text-xs sm:text-sm text-zinc-600 dark:text-zinc-400 truncate">{pedido.vendedor}</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Informações do Cliente */}
              <div className="space-y-3 sm:space-y-4">
                <h3 className="text-base sm:text-lg font-semibold text-zinc-800 dark:text-zinc-200 flex items-center gap-2">
                  <UserIcon className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-500" />
                  Dados do Cliente
                </h3>
                
                <div className="space-y-2 sm:space-y-3">
                  <div className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg bg-zinc-50 dark:bg-zinc-700/50">
                    <UserIcon className="h-3 w-3 sm:h-4 sm:w-4 text-zinc-500 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-xs sm:text-sm font-medium text-zinc-700 dark:text-zinc-300">Cliente</div>
                      <div className="text-xs sm:text-sm text-zinc-600 dark:text-zinc-400 truncate">{pedido.cliente}</div>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg bg-zinc-50 dark:bg-zinc-700/50">
                    <MapPinIcon className="h-3 w-3 sm:h-4 sm:w-4 text-rose-500 flex-shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1">
                      <div className="text-xs sm:text-sm font-medium text-zinc-700 dark:text-zinc-300">Localização</div>
                      <div className="text-xs sm:text-sm text-zinc-600 dark:text-zinc-400">
                        {pedido.cidade}, {pedido.estado}
                      </div>
                      {pedido.endereco && (
                        <div className="text-xs text-zinc-500 mt-1">
                          {pedido.endereco}, {pedido.numero}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Observações */}
              {pedido.observacao && (
                <div className="col-span-full">
                  <h3 className="text-base sm:text-lg font-semibold text-zinc-800 dark:text-zinc-200 mb-2 sm:mb-3">Observações</h3>
                  <div className="p-3 sm:p-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                    <p className="text-zinc-700 dark:text-zinc-300">{pedido.observacao}</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'produtos' && (
            <div>
              <h3 className="text-base sm:text-lg font-semibold text-zinc-800 dark:text-zinc-200 mb-3 sm:mb-4 flex items-center gap-2">
                <CubeIcon className="h-4 w-4 sm:h-5 sm:w-5 text-blue-500" />
                Produtos do Pedido
              </h3>
              
              {loadingProdutos ? (
                <div className="flex items-center justify-center py-6 sm:py-8">
                  <ArrowPathIcon className="h-6 w-6 sm:h-8 sm:w-8 animate-spin text-blue-500" />
                  <span className="ml-2 text-sm sm:text-base text-zinc-600 dark:text-zinc-400">Carregando produtos...</span>
                </div>
              ) : produtos && produtos.length > 0 ? (
                <div className="space-y-2 sm:space-y-3">
                  {produtos.map((produto, idx) => (
                    <div key={idx} className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 sm:p-4 rounded-lg border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-700/50 gap-2 sm:gap-4">
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-sm sm:text-base text-zinc-800 dark:text-zinc-200 truncate">
                          {produto.descricao}
                        </h4>
                        <p className="text-xs sm:text-sm text-zinc-500 dark:text-zinc-400">
                          Código: {produto.produto}
                        </p>
                      </div>
                      <div className="flex sm:flex-col sm:text-right gap-2 sm:gap-0">
                        <div className="font-semibold text-xs sm:text-sm text-zinc-800 dark:text-zinc-200">
                          Qtd: {produto.qtde}
                        </div>
                        <div className="text-xs sm:text-sm text-emerald-600 dark:text-emerald-400 font-medium">
                          R$ {parseFloat(produto.prvendido?.toString() || '0').toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 sm:py-8 text-zinc-500 dark:text-zinc-400">
                  <CubeIcon className="h-8 w-8 sm:h-12 sm:w-12 mx-auto mb-2 sm:mb-3 opacity-50" />
                  <p>Nenhum produto encontrado para este pedido</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'contas' && (
            <div>
              <h3 className="text-base sm:text-lg font-semibold text-zinc-800 dark:text-zinc-200 mb-3 sm:mb-4 flex items-center gap-2">
                <CreditCardIcon className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-500" />
                Contas a Receber
              </h3>
              
              {loadingContas ? (
                <div className="flex items-center justify-center py-6 sm:py-8">
                  <ArrowPathIcon className="h-6 w-6 sm:h-8 sm:w-8 animate-spin text-emerald-500" />
                  <span className="ml-2 text-sm sm:text-base text-zinc-600 dark:text-zinc-400">Carregando contas...</span>
                </div>
              ) : contas && contas.length > 0 ? (
                <div className="space-y-2 sm:space-y-3">
                  {contas.map((conta, idx) => (
                    <div key={idx} className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 sm:p-4 rounded-lg border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-700/50 gap-2 sm:gap-4">
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-sm sm:text-base text-zinc-800 dark:text-zinc-200 truncate">
                          Documento: {conta.documento}
                        </h4>
                        <p className="text-xs sm:text-sm text-zinc-500 dark:text-zinc-400">
                          Parcela: {conta.parcela}
                        </p>
                        <p className="text-xs sm:text-sm text-zinc-500 dark:text-zinc-400">
                          Vencimento: {new Date(conta.vencimento).toLocaleDateString('pt-BR')}
                        </p>
                      </div>
                      <div className="flex sm:flex-col sm:text-right items-center sm:items-end gap-2 sm:gap-1">
                        <div className={`font-semibold text-sm sm:text-base ${
                          conta.diasatrazo && conta.diasatrazo > 0 
                            ? 'text-rose-600 dark:text-rose-400' 
                            : 'text-emerald-600 dark:text-emerald-400'
                        }`}>
                          R$ {parseFloat(conta.valorpendente?.toString() || '0').toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </div>
                        {conta.diasatrazo && conta.diasatrazo > 0 && (
                          <div className="text-xs sm:text-sm text-rose-500">
                            {conta.diasatrazo}d atraso
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 sm:py-8 text-zinc-500 dark:text-zinc-400">
                  <CreditCardIcon className="h-8 w-8 sm:h-12 sm:w-12 mx-auto mb-2 sm:mb-3 opacity-50" />
                  <p className="text-sm sm:text-base">Nenhuma conta a receber encontrada para este cliente</p>
                  <p className="text-xs sm:text-sm mt-1 sm:mt-2">Cliente: {pedido.codigocliente}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PedidoDetalhes;